import React, { useState, useEffect, useCallback } from 'react'
import Modal from '@/v2/components/common/Modal'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import {
  useAddClaim,
  useUpdateClaimInfo,
  useShowClaim,
} from '@/v2/hooks/insurance/useClaims'
import {
  generateEmptyProcedure,
  generateEmptyProvider,
  normalizeGender,
} from './utils'
import Form from './claim-form/Form'
import { validateClaim as validateClaimValidator } from './claim-form/validator'
import Edit from './claim-form/Edit'
import useModals from '@/v2/hooks/useModals'
import { modalTitle, ctaButtonTitle, modalHeihgt } from './utils'
import Prompt from '@/v2/components/common/Prompt'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useValidateClaim } from '@/v2/hooks/insurance/useClaims'
import { useShowEligibility } from '@/v2/hooks/insurance/useEligibilities'
import useDetectDevice from '@/v2/hooks/useDetectDevice'
import { toApiDateString } from '@/v2/utils/dateHelpers'
import ClaimModalHeader from './ClaimModalHeader'

const ClaimForm = ({
  isOpen,
  onClose,
  modalType,
  insurance,
  isEdit,
  patientId,
  insuranceId,
  claim,
  patient,
  eligibility,
  scrollToHistory,
  justCreated = false,
}) => {
  const { goTo } = useModals()
  const { isMobile } = useDetectDevice()
  const { addClaim } = useAddClaim(patientId)
  const { updateClaim } = useUpdateClaimInfo(
    claim?.insurance?.id,
    claim?.id,
    patientId
  )

  const [values, setValues] = useState({
    insuranceId: '',
    subscriber: {
      subscriber: '',
      dob: '',
      firstName: '',
      lastName: '',
      gender: '',
      relationship: '',
      sequenceCode: '',
      memberId: '',
      groupNumber: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipcode: '',
    },
    providers: [generateEmptyProvider()],
    procedure: [generateEmptyProcedure()],
  })
  const [error, setError] = useState({})
  const { onOpenSnackbar } = useSnackbarContext()
  const [showButtons, setShowButtons] = useState(true)
  const [showAlert, setShowAlert] = useState(false)
  const { validateClaim } = useValidateClaim(claim?.id, patientId)
  const claimStatus = claim?.status
  // Shares the ['ShowClaim', claimId] cache entry Edit already fetches, so this
  // only reads the DXC id off the full claim rather than issuing a request.
  const { claimInfo } = useShowClaim(insuranceId, claim?.id)

  const handleChange = useCallback(value => {
    setValues(prev => {
      const next = { ...prev, ...value }
      if (value.subscriber) {
        next.subscriber = { ...prev.subscriber, ...value.subscriber }
      }
      return next
    })
  }, [])

  // Fetch full eligibility data if eligibility ID is provided
  const { eligibilityInfo, isLoading: eligibilityLoading } = useShowEligibility(
    eligibility?.insurance?.id,
    eligibility?.id
  )

  // Initialize form values when claim data is available
  useEffect(() => {
    if (claim && (modalType === 'view' || modalType === 'edit')) {
      setValues({
        insuranceId: claim.insurance?.id || '',
        subscriber: {
          subscriber: claim.subscriber?.subscriber || '',
          dob: toApiDateString(claim.subscriber?.dob),
          firstName: claim.subscriber?.firstName || '',
          lastName: claim.subscriber?.lastName || '',
          gender: claim.subscriber?.gender || '',
          relationship: claim.subscriber?.relationship || '',
          sequenceCode: claim.subscriber?.sequenceCode || '',
          memberId: claim.subscriber?.memberId || '',
        },
        providers: claim.providers?.length
          ? claim.providers
          : [generateEmptyProvider()],
        procedure: claim.procedure?.length
          ? claim.procedure
          : [generateEmptyProcedure()],
      })
    } else if (modalType === 'create') {
      setValues(prev => ({
        ...prev,
        insuranceId: insurance?.id || prev.insuranceId,
      }))
    }
  }, [claim, modalType, insurance?.id])

  useEffect(() => {
    if (
      modalType === 'create' &&
      !eligibilityLoading &&
      (eligibilityInfo?.response || eligibility?.response)
    ) {
      const eligibilitySubscriber =
        eligibilityInfo?.response?.subscriber ||
        eligibility?.response?.subscriber
      const eligibilityPatient =
        eligibilityInfo?.response?.patient || eligibility?.response?.patient

      setValues(prevValues => ({
        ...prevValues,
        insuranceId: insurance?.id || prevValues.insuranceId,
        subscriber: {
          ...prevValues.subscriber,
          dob:
            toApiDateString(
              eligibilitySubscriber?.dateOfBirth ||
                eligibilitySubscriber?.dob ||
                eligibilityPatient?.dateOfBirth ||
                eligibilityPatient?.dob
            ) || prevValues.subscriber.dob,
          firstName:
            eligibilitySubscriber?.firstName ||
            eligibilityPatient?.firstName ||
            prevValues.subscriber.firstName,
          lastName:
            eligibilitySubscriber?.lastName ||
            eligibilityPatient?.lastName ||
            prevValues.subscriber.lastName,
          gender: normalizeGender(
            eligibilitySubscriber?.gender ||
              eligibilitySubscriber?.sex ||
              eligibilityPatient?.gender ||
              eligibilityPatient?.sex ||
              prevValues.subscriber.gender
          ),
          memberId:
            eligibilitySubscriber?.memberId ||
            eligibilityPatient?.memberId ||
            eligibilityInfo?.response?.healthplan?.plan?.subscriberId ||
            eligibility?.response?.healthplan?.plan?.subscriberId ||
            insurance?.memberId ||
            prevValues.subscriber.memberId,
          groupNumber:
            eligibilitySubscriber?.groupNumber ||
            eligibilitySubscriber?.group ||
            eligibilityPatient?.groupNumber ||
            eligibilityPatient?.group ||
            eligibilityInfo?.response?.healthplan?.plan?.groupNumber ||
            eligibility?.response?.healthplan?.plan?.groupNumber ||
            prevValues.subscriber.groupNumber ||
            '',
          address1:
            eligibilitySubscriber?.address?.address1 ||
            eligibilityPatient?.address?.address1 ||
            prevValues.subscriber.address1 ||
            '',
          address2:
            eligibilitySubscriber?.address?.address2 ||
            eligibilityPatient?.address?.address2 ||
            prevValues.subscriber.address2 ||
            '',
          city:
            eligibilitySubscriber?.address?.city ||
            eligibilityPatient?.address?.city ||
            prevValues.subscriber.city ||
            '',
          state:
            eligibilitySubscriber?.address?.state ||
            eligibilityPatient?.address?.state ||
            prevValues.subscriber.state ||
            '',
          zipcode:
            eligibilitySubscriber?.address?.zipcode ||
            eligibilityPatient?.address?.zipcode ||
            prevValues.subscriber.zipcode ||
            '',
        },
      }))
    }
  }, [
    modalType,
    eligibilityLoading,
    eligibilityInfo?.response,
    eligibility?.response,
    insurance?.id,
  ])

  const editTriggered = value => {
    setShowAlert(value)
    setShowButtons(value)
  }

  useEffect(() => {
    if (modalType === 'view' || modalType === 'edit') {
      setShowButtons(false)
    }
  }, [modalType])

  const handleOnClose = () => {
    if (!isEdit || !showAlert) {
      setValues({
        insuranceId: '',
        subscriber: {
          subscriber: '',
          dob: '',
          firstName: '',
          lastName: '',
          gender: '',
          relationship: '',
          sequenceCode: '',
          memberId: '',
          groupNumber: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          zipcode: '',
        },
        providers: [generateEmptyProvider()],
        procedure: [generateEmptyProcedure()],
      })
    }
    onClose()
  }

  const handleFormComplete = () => {
    setValues({
      insuranceId: '',
      subscriber: {
        subscriber: '',
        dob: '',
        firstName: '',
        lastName: '',
        gender: '',
        relationship: '',
        sequenceCode: '',
        memberId: '',
        groupNumber: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipcode: '',
      },
      providers: [generateEmptyProvider()],
      procedure: [generateEmptyProcedure()],
    })
    onClose()
  }

  const handleSave = async body => {
    const res = await addClaim({
      insuranceId: values.insuranceId,
      body,
    })
    if (!res.error) {
      onOpenSnackbar('Created claim', 'success')
      handleFormComplete()
      goTo(`v2/patient/${patientId}/claim/edit`, {
        modalType: 'edit',
        patientId,
        insuranceId: values.insuranceId,
        isEdit: true,
        claim: res,
        patient,
        justCreated: true,
      })
    }
  }

  const handleEdit = async body => {
    setShowAlert(false)
    const res = await updateClaim({
      insuranceId: values.insuranceId,
      body,
    })
    if (!res.error) {
      onOpenSnackbar('Claim successfully edited', 'success')
      handleFormComplete()
    }
  }

  const handleSubmit = async () => {
    if (modalType === 'view') {
      onClose()
      return
    }

    const validErrors = await validateClaimValidator(values)
    setError(validErrors || {})

    if (!validErrors) {
      try {
        const { subscriber, ...subscriberInfo } = values.subscriber
        const data = {
          subscriberAttributes: subscriberInfo,
        }
        const body = {
          claim: {
            ...data,
          },
        }
        if (modalType === 'edit') {
          await handleEdit(body)
        }

        if (modalType === 'create') {
          await handleSave(body)
        }
      } catch (error) {
        onOpenSnackbar(`${error}`, 'error')
      }
    }
  }

  const handleValidateClaim = async () => {
    try {
      const { error } = await validateClaim()
      if (!error) {
        onOpenSnackbar('Claim validated', 'success')
        handleFormComplete()
      }
    } catch (error) {
      onOpenSnackbar('Error validating claim', 'error')
    }
  }

  const modalContent = {
    create: (
      <Form
        isEdit={false}
        values={values}
        error={error}
        onChange={handleChange}
        patient={patient}
        eligibility={eligibilityInfo}
      />
    ),
    view: (
      <Edit
        claim={claim}
        patientId={patientId}
        insuranceId={insuranceId}
        onTriggerEdit={editTriggered}
        error={error}
        onChange={handleChange}
        patient={patient}
        viewOnly={true}
        scrollToHistory={scrollToHistory}
      />
    ),
    edit: (
      <Edit
        claim={claim}
        patientId={patientId}
        insuranceId={insuranceId}
        onTriggerEdit={editTriggered}
        error={error}
        onChange={handleChange}
        patient={patient}
        scrollToHistory={scrollToHistory}
        justCreated={justCreated}
      />
    ),
  }

  return (
    <>
      <Prompt
        when={showAlert}
        message="You have unsaved changes, are you sure you want to leave?"
      />
      <Modal
        isOpen={isOpen}
        title={modalTitle[modalType]}
        header={
          <ClaimModalHeader
            title={modalTitle[modalType]}
            claimId={claim?.id}
            dxcClaimId={claimInfo?.dxcClaimId}
          />
        }
        maxWidth="md"
        confirmLabel={ctaButtonTitle[modalType]}
        onConfirm={() => handleSubmit()}
        showConfirm={showButtons}
        showClose={showButtons}
        onClose={handleOnClose}
        fullScreen={isMobile}
        fixedHeight={isMobile ? undefined : modalHeihgt[modalType]}
        showExtraButton={
          modalType === 'edit' &&
          (claimStatus === 'pending' || claimStatus === 'invalidated')
        }
        extraButtonLabel="Validate Claim"
        extraButtonVariant="contained"
        extraButtonColor="secondary"
        onExtraButtonClick={() => handleValidateClaim()}
      >
        {modalContent[modalType]}
        {isEdit && showAlert && (
          <Grid item xs={12} mt={2}>
            <Box display={'flex'} flexDirection={'row-reverse'}>
              <Typography variant="h5" color="error">
                You have unsaved changes. Please save before closing.
              </Typography>
            </Box>
          </Grid>
        )}
      </Modal>
    </>
  )
}

export default ClaimForm
